import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  OfficePracticeAreaSettingDto,
  UpsertOfficePracticeAreaSettingRequest,
} from '../models/office-practice-area-setting.models';

/**
 * Wraps the per-office, per-practice-area capacity settings API. These two actions live on
 * `OfficesController` rather than a controller of their own, so the base URL is `/api/offices`
 * — not an `/api/officepracticeareasettings` route, which does not exist.
 */
@Injectable({
  providedIn: 'root',
})
export class OfficePracticeAreaSettingsService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/offices`;

  /**
   * Get the configured practice-area settings for an office
   * (GET /api/offices/office/{officeCode}/practice-area-settings).
   *
   * Returns only pairings that have a row, with every setting on it populated. An empty list
   * means every practice area in the office takes the defaults (expected hours not required,
   * a one-week capacity period) — it is not an error state.
   */
  getByOffice(officeCode: string): Observable<OfficePracticeAreaSettingDto[]> {
    return this.http
      .get<OfficePracticeAreaSettingDto[]>(
        `${this.apiUrl}/office/${encodeURIComponent(officeCode)}/practice-area-settings`,
      );
  }

  /**
   * Create or update the settings for an office + practice area
   * (PUT /api/offices/practice-area-settings).
   *
   * Upsert keyed by the (officeCode, practiceAreaCode) pair in the body, so callers needn't
   * track create vs update and there is no parent row to create first. Turning a pairing off
   * is this same call with `expectedHoursIsRequired: false`.
   *
   * **Partial update:** the body carries only the settings it means to change, and anything it
   * omits keeps its stored value (see `UpsertOfficePracticeAreaSettingRequest`). Send one
   * setting per call — a body carrying both republishes whatever this client last read of the
   * other. Omitting both is a 400.
   */
  upsert(
    request: UpsertOfficePracticeAreaSettingRequest,
  ): Observable<OfficePracticeAreaSettingDto> {
    return this.http
      .put<OfficePracticeAreaSettingDto>(
        `${this.apiUrl}/practice-area-settings`,
        request,
      );
  }
}
